import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Box, Text, Tooltip, useMantineTheme } from "@mantine/core";
import { ReusableTable } from "../../Shared/table";
import { useLocation, useNavigate } from "react-router-dom";
import useColors from "../../../theme/useColors";
import SimpleCircularIndicator from "../../Shared/indicator";
import UnreadMessages from "../../../../src/assets/UnreadMessage.svg";
import ReadMessages from "../../../../src/assets/ReadMessage.svg";
import { getMessageDetailsRoute } from "../../../constants/module-nav";
import PaginationComponent from "../../Shared/Pagination";
import FilterComponent from "../../Filter";
import {
  convertToSelectOptions,
  formatDate,
  formatTimeWithZoneAbbreviation,
} from "../../../utils";
import {
  DATE_FORMAT,
  MESSAGE_STATUS_OPTION,
} from "../../../constants/app-constant";
import {
  resetFilters,
  setConversationList,
  setMessageCount,
  setSearchValue,
  setSelectedPage,
  setSelectedStatus,
  setSelectedTopic,
} from "../../../store/messageReducer";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { parseUnreadConversationCount } from "../../../hooks/queries";
import { useMediaQuery } from "@mantine/hooks";
import type { MessageData } from "../../../types/message";
import {
  useConversationList,
  useTopicList,
  useUnreadConversationCount,
} from "../../../hooks/queries";

export type { MessageData } from "../../../types/message";

const limit = 15;

export default function MessageTable() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const colors = useColors();

  const conversationList = useAppSelector(
    (state) => state?.messages?.conversationList
  );
  const { page, selectedStatus, selectedTopic, searchValue } = useAppSelector(
    (state) => state?.messages?.messageFilters
  );
  const provider = useAppSelector((state) => state?.provider?.provider);
  const providerNPI = useAppSelector((state) => state?.provider?.providerNPI);

  const theme = useMantineTheme();
  const isSmallScreen = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);

  const [totalPages, setTotalPages] = useState<number>(0);
  const [statusOptions] = useState<any[]>(MESSAGE_STATUS_OPTION);
  const [topicOptions, setTopicOptions] = useState<any[]>([]);
  const [topicsInitialized, setTopicsInitialized] = useState(false);

  const { data: topicResponse, isLoading: topicLoading } = useTopicList();

  useEffect(() => {
    if (topicResponse?.data) {
      const options = convertToSelectOptions(topicResponse.data);
      const allTopicValues = options?.map((option: any) => option?.value);
      setTopicOptions(options);
      setTopicsInitialized(true);
      if (selectedTopic.length === 0) {
        dispatch(setSelectedTopic(allTopicValues));
      }
    }
  }, [dispatch, selectedTopic.length, topicResponse]);

  // Note: intentionally does NOT require selectedTopic.length > 0 - an
  // empty/unselected topic filter means "no filter" (see conversationParams
  // below), not "don't fetch". Gating on a non-empty topic list would leave
  // the conversation list permanently empty whenever the topic reference
  // data comes back empty (e.g. no topics configured for this provider).
  const conversationQueryEnabled = topicsInitialized && Boolean(providerNPI);

  const conversationParams = useMemo(
    () => ({
      status: selectedStatus?.length > 0 ? selectedStatus?.join(",") : "",
      topic_id: selectedTopic?.length > 0 ? selectedTopic?.join(",") : "",
      provider_npi: providerNPI,
      limit: 15,
      page: Number(page) - 1,
      search: searchValue,
    }),
    [page, providerNPI, searchValue, selectedStatus, selectedTopic]
  );

  const {
    data: conversationResponse,
    isLoading: conversationLoading,
    isFetching,
  } = useConversationList(conversationParams, {
    enabled: conversationQueryEnabled,
    polling: conversationQueryEnabled,
  });

  const initialLoading =
    (topicLoading && !topicsInitialized) ||
    (conversationQueryEnabled && conversationLoading);

  const { refetch: refetchUnreadCount } = useUnreadConversationCount(providerNPI);

  useEffect(() => {
    if (conversationResponse?.status === 200) {
      dispatch(setConversationList(conversationResponse?.data?.data));
      setTotalPages(Math.ceil(conversationResponse?.data.total_count / limit));

      const filteredCount = parseUnreadConversationCount(
        conversationResponse?.data
      );
      if (filteredCount > 0) {
        dispatch(setMessageCount(filteredCount));
      } else if (providerNPI) {
        void refetchUnreadCount().then((result) => {
          if (result.data !== undefined) {
            dispatch(setMessageCount(result.data));
          }
        });
      }
    }
  }, [conversationResponse, dispatch, providerNPI, refetchUnreadCount]);

  const isRefreshing = isFetching && !initialLoading;

  const handleEdit = (row: MessageData) => {
    console.log("Edit row:", row);
  };

  const handleDownload = (row: MessageData) => {
    console.log("Download row:", row);
  };

  const handlePrint = (row: MessageData) => {
    console.log("Print row:", row);
  };

  const handleRowClick = (row: any) => {
    navigate(getMessageDetailsRoute(location.pathname, row?.CONVERSATION_ID), {
      state: { conversationData: row },
    });
  };

  const statusColors = {
    OPEN: colors.warning[4],
    CLOSED: colors.success[6],
    open: colors.warning[4],
    closed: colors.success[6],
  };

  const messageColumns = [
    {
      header: "Query ID",
      align: "left" as const,
      width: "200px",
      accessor: (row: MessageData) => (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <Tooltip
            label={`${row.QUERY_ID}`}
            position='bottom-start'
            withArrow
            multiline
            arrowPosition='side'
            arrowOffset={5}
            arrowSize={5}
            arrowRadius={3}
          >
            <div style={{ minWidth: isSmallScreen ? 170 : 200 }}>
              <Text size={isSmallScreen ? "12px" : "14px"}>{row.QUERY_ID}</Text>
            </div>
          </Tooltip>
          {Number(row?.UNREAD_COUNT) > 0 ? (
            <SimpleCircularIndicator
              count={Number(row?.UNREAD_COUNT)}
              color={colors.danger[4]}
              size={16}
              right={6}
            >
              <Box
                component='img'
                src={UnreadMessages}
                alt='unread messages'
                height={18}
                width={18}
              />
            </SimpleCircularIndicator>
          ) : (
            <Box
              component='img'
              src={ReadMessages}
              alt='read messages'
              height={isSmallScreen ? 18 : 20}
              width={isSmallScreen ? 18 : 20}
            />
          )}
        </div>
      ),
    },
    {
      header: "Patient Name/MRN",
      align: "left" as const,
      accessor: (row: MessageData) => (
        <Tooltip
          label={
            <div>
              <div>Patient: {row.PATIENT_NAME}</div>
              <div>MRN: {row.MRN_NO}</div>
            </div>
          }
          position='bottom-start'
          withArrow
          multiline
          arrowPosition='side'
          arrowOffset={5}
          arrowSize={5}
          arrowRadius={3}
        >
          <div>
            <Text
              size={isSmallScreen ? "12px" : "14px"}
              fw={500}
              c={colors.gray[8]}
            >
              {row.PATIENT_NAME}
            </Text>
            <Text size='xs' c={colors.gray[6]} fw={500} mt={"2px"}>
              {row.MRN_NO}
            </Text>
          </div>
        </Tooltip>
      ),
      width: "200px",
    },
    {
      header: "Topic",
      align: "left" as const,
      accessor: "TOPIC" as keyof MessageData,
      width: "200px",
    },
    {
      header: "Status",
      align: "left" as const,
      accessor: "STATUS" as keyof MessageData,
      width: "120px",
      cellType: "status" as const,
    },
    {
      header: "Date & Time",
      align: "left" as const,
      accessor: (row: MessageData) => (
        <Tooltip
          arrowPosition='side'
          arrowOffset={5}
          arrowSize={5}
          arrowRadius={3}
          label={
            <div style={{ textAlign: "left" }}>
              <div>{formatDate(row?.LAST_UPDATED, DATE_FORMAT.MM_DD_YYYY)}</div>
              <div>
                {formatTimeWithZoneAbbreviation(
                  row?.LAST_UPDATED,
                  provider?.timezone_info?.timezone_name,
                  provider?.timezone_info?.abbrev_simple
                )}
              </div>
            </div>
          }
          position='bottom-start'
          withArrow
          multiline
        >
          <Box style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Text
              size={isSmallScreen ? "12px" : "14px"}
              fw={500}
              c={colors.gray[8]}
            >
              {formatDate(row?.LAST_UPDATED, DATE_FORMAT.MM_DD_YYYY)}
            </Text>
            <Text size={"12px"} fw={400} c='dimmed'>
              {formatTimeWithZoneAbbreviation(
                row?.LAST_UPDATED,
                provider?.timezone_info?.timezone_name,
                provider?.timezone_info?.abbrev_simple
              )}
            </Text>
          </Box>
        </Tooltip>
      ),
      width: "200px",
    },
  ];

  useLayoutEffect(() => {
    dispatch(resetFilters());
    dispatch(setConversationList([]));
  }, [dispatch]);

  useEffect(() => {
    if (page !== 1) {
      dispatch(setSelectedPage(1));
    }
    // Reset to first page when filters change; page intentionally omitted to avoid loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedStatus, selectedTopic, searchValue]);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box style={{ paddingBottom: "1px", flexShrink: 0 }}>
        <FilterComponent
          loading={topicLoading && topicOptions.length === 0}
          refreshing={isRefreshing && !initialLoading}
          dropDown1Options={statusOptions}
          dropdown1Values={selectedStatus}
          dropdown1PlaceHolder='Select Message Status'
          setDropdown1Values={(val) => dispatch(setSelectedStatus(val))}
          dropDown2Options={topicOptions}
          dropdown2Values={selectedTopic}
          setDropdown2Values={(val) => dispatch(setSelectedTopic(val))}
          searchValue={searchValue}
          setSearchValue={(val) => dispatch(setSearchValue(val))}
          hideThirdDropdown={true}
          debounceDelay={500}
        />
      </Box>

      <ReusableTable
        data={conversationList}
        columns={messageColumns}
        getRowId={(row) => `${row?.CONVERSATION_ID}`}
        onEdit={handleEdit}
        onDownload={handleDownload}
        onPrint={handlePrint}
        showActionButtons={false}
        showSeriesNumber
        statusColors={statusColors}
        onRowClick={handleRowClick}
        loading={initialLoading}
      />
      <Box
        style={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "flex-end",
          padding: "4px 4px",
        }}
      >
        <PaginationComponent
          total={totalPages}
          value={Number(page)}
          onChange={(val) => dispatch(setSelectedPage(val))}
          disabled={initialLoading || isRefreshing}
        />
      </Box>
    </div>
  );
}
